import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { onlineOrderingLinks } from "@/components/lib/SidebarLinks";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchAllWebsiteSettings } from "@/redux/slice/order-ordering-slice/websiteSettingsSlice";
import {
  clearTillOrderAccessMessages,
  fetchTillOrderAccessTills,
  saveTillOrderAccess,
  setSelectedTillId,
} from "@/redux/slice/order-ordering-slice/till-order-access-slice";
import useAuth from "@/hooks/useAuth";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";

function TillOrderAccess() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const selectionInitializedRef = useRef(false);
  const { tills, selectedTillId, getLoading, saveLoading, success, error } =
    useSelector((state) => state.onlineOrdering.tillOrderAccess);
  const { websiteSettings, getLoading: settingsLoading } = useSelector(
    (state) => state.onlineOrdering.websiteSettings,
  );

  useEffect(() => {
    if (user?.restaurant_id) {
      dispatch(fetchAllWebsiteSettings(user.restaurant_id));
    }
    dispatch(fetchTillOrderAccessTills());
  }, [dispatch, user?.restaurant_id]);

  useEffect(() => {
    if (selectionInitializedRef.current) {
      return;
    }

    if (settingsLoading || getLoading) {
      return;
    }

    if (!Array.isArray(tills) || tills.length === 0) {
      return;
    }

    const savedTillId = websiteSettings?.setting?.online_order_till_id;
    const matchedTill = tills.find(
      (till) => Number(till.id) === Number(savedTillId),
    );
    const nextSelectedTillId = matchedTill?.id ?? tills[0]?.id;

    if (nextSelectedTillId) {
      dispatch(setSelectedTillId(String(nextSelectedTillId)));
      selectionInitializedRef.current = true;
    }
  }, [dispatch, getLoading, settingsLoading, tills, websiteSettings?.setting?.online_order_till_id]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      dispatch(clearTillOrderAccessMessages());
    }
  }, [dispatch, success]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearTillOrderAccessMessages());
    }
  }, [dispatch, error]);

  const handleSave = async () => {
    if (!selectedTillId) {
      toast.error("Please select a till");
      return;
    }

    if (!user?.restaurant_id) {
      toast.error("Restaurant information not available");
      return;
    }

    await dispatch(
      saveTillOrderAccess({
        restaurantId: user.restaurant_id,
        tillId: Number(selectedTillId),
      }),
    );
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={onlineOrderingLinks} />}
      pageName={{ title: "Till Order Access" }}
      paths={[
        // { name: "Home", path: "/home" },
        // { name: "Online Ordering", path: "/online-ordering" },
        {
          name: "Settings",
          path: "/onlineordering/settings/website-settings",
        },
        { name: "Till Order Access" },
      ]}
    >
      <div className="container mt-5">
        <Card className="bg-cblack3 border-0 rounded-sm">
          <CardContent className="p-6 md:p-7">
            <h3 className="text-cwhite text-lg font-medium leading-tight mb-6">
              Select Till For Online Order
            </h3>

            {getLoading ? (
              <div className="space-y-4 mb-7">
                <Skeleton className="h-5 w-44 bg-cblack7" />
                <Skeleton className="h-5 w-40 bg-cblack7" />
              </div>
            ) : (
              <RadioGroup
                className="flex flex-wrap items-center gap-8 md:gap-10 mb-8"
                value={selectedTillId}
                onValueChange={(value) => dispatch(setSelectedTillId(value))}
              >
                {Array.isArray(tills) && tills.length > 0 ? (
                  tills.map((till) => (
                    <div key={till.id} className="flex items-center space-x-2">
                      <RadioGroupItem
                        value={String(till.id)}
                        id={`till-${till.id}`}
                        className="h-[18px] w-[18px] border-cwhite4"
                      />
                      <Label
                        htmlFor={`till-${till.id}`}
                        className="text-cwhite text-base font-normal cursor-pointer"
                      >
                        {`${till?.device_name} (${till?.id})` || `Till ${till.id}`}
                      </Label>
                    </div>
                  ))
                ) : (
                  <p className="text-cwhite3 text-sm">No till found</p>
                )}
              </RadioGroup>
            )}

            <Button
              className="bg-cgreen hover:bg-cgreen/90 text-white"
              onClick={handleSave}
              isLoading={saveLoading}
              disabled={saveLoading || getLoading || settingsLoading || !selectedTillId}
            >
              Save
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout2>
  );
}

export default TillOrderAccess;
